import { Button, tokens } from "@blurb/codex-react";
import { createStyles } from "@mantine/core";
import { useForm } from "@mantine/form";
import { GraphQL, GraphQLQueryData } from "@rpi/openapi-api";
import { useQueryClient } from "@tanstack/react-query";
import React from "react";
import { RpaButtonLink } from "../../../components/RpiLink";
import { RpaDropdown } from "../../../components/inputFields/RpaDropdownInput";
import { RpaTextInput } from "../../../components/inputFields/RpaTextInput";
import { NavigateRoute } from "../../../hooks/useNavigateRoute";
import { RpaDrawer, RpaDrawerOverlay } from "../../../rpi-core/drawer/RpaDrawer";
import { RpaDrawerField } from "../../../rpi-core/drawer/RpaDrawerField";
import { createDrawerOverlay, createRpiDrawerView, RpiDrawerProps } from "../../../rpi-core/drawer/RpiDrawer";
import { countrySelectData, getCountryLabel } from "../../../utils/country.util";
import { shouldRetry } from "../../../utils/query.util";
import {
  buildOrganizationUpdateInput,
  canEditAnyOrganizationField,
  isOrganizationFieldEditable,
  isValidWebsiteUrl,
  normalizeWebsiteUrl,
  ORGANIZATION_SIZE_OPTIONS,
} from "./organizationFields";

const FORM_ID = "settings-edit-organization-form";

const useStyles = createStyles({
  body: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacing[4],
    padding: `0 ${tokens.spacing[6]}`,
  },
  footer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: tokens.spacing[2],
    padding: tokens.spacing[6],
  },
});

export const SettingsEditOrganizationInformationView = createRpiDrawerView<Pick<RpiDrawerProps, "opened" | "onClose">>(
  ({ opened, onClose }) => {
    const [closing, setClosing] = React.useState(false);

    const handleClose = React.useCallback(
      (delay?: boolean) => {
        if (closing) return;

        setClosing(true);
        if (delay) {
          return setTimeout(onClose, 1000);
        }
        return onClose();
      },
      [onClose, closing]
    );

    const { data, isLoading, error } = GraphQL.useDashboardUserCurrentQuery<GraphQL.DashboardUserCurrentQuery, unknown>(
      {},
      { enabled: opened, refetchOnMount: true, retry: shouldRetry(handleClose), cacheTime: 0 }
    );
    const dashboardUser = data?.dashboardUserCurrent;

    // Entry guard for deep links into a nothing-to-edit route. Latched on first load so the
    // post-save refetch (now all-provided) can't redirect mid-success, before the overlay/close.
    const canEnterRef = React.useRef<boolean>();
    if (dashboardUser && canEnterRef.current === undefined) {
      canEnterRef.current = canEditAnyOrganizationField(
        dashboardUser.customer?.organization,
        dashboardUser.role || undefined
      );
    }

    if (canEnterRef.current === false) {
      return <NavigateRoute route={["settings"]} replace />;
    }

    return (
      <RpaDrawer
        title="Edit Organization Information"
        isLoading={isLoading}
        error={error}
        opened={opened}
        onClose={() => handleClose()}
      >
        {dashboardUser && (
          <EditOrganizationInformation
            dashboardUser={dashboardUser}
            onSuccess={() => handleClose(true)}
            onCancel={() => handleClose()}
          />
        )}
      </RpaDrawer>
    );
  }
);

interface EditOrganizationInformationProps {
  dashboardUser: GraphQLQueryData<GraphQL.DashboardUserCurrentQuery>;
  onSuccess: () => void;
  onCancel: () => void;
}

const EditOrganizationInformation: React.FC<EditOrganizationInformationProps> = ({
  dashboardUser,
  onSuccess,
  onCancel,
}) => {
  const { classes } = useStyles();
  const queryClient = useQueryClient();

  const role = dashboardUser.role || undefined;
  const organization = dashboardUser.customer?.organization;
  const organizationName = dashboardUser.customer?.organizationName;

  const websiteEditable = isOrganizationFieldEditable(organization?.website, role);
  const sizeEditable = isOrganizationFieldEditable(organization?.size, role);
  const countryEditable = isOrganizationFieldEditable(organization?.country, role);

  const [isSaving, setIsSaving] = React.useState(false);
  const {
    mutateAsync: editOrganizationInformation,
    reset,
    ...mutation
  } = GraphQL.useEditOrganizationInformationMutation<{ message: string }>();

  const form = useForm<GraphQL.EditOrganizationInformationMutationVariables["input"]>({
    validateInputOnBlur: true,
    initialValues: {
      website: organization?.website ?? "",
      size: organization?.size ?? "",
      country: organization?.country ?? "",
    },
    validate: {
      website: (value?: string | null): string | null =>
        isValidWebsiteUrl(value ?? "") ? null : "Please enter a valid website URL",
    },
  });

  React.useEffect(() => reset, [reset]);

  const handleSubmit = React.useCallback(
    async (values: GraphQL.EditOrganizationInformationMutationVariables["input"]) => {
      setIsSaving(true);
      const website = values.website?.trim() ? normalizeWebsiteUrl(values.website) : values.website;
      const input = buildOrganizationUpdateInput(
        { ...values, website },
        { website: websiteEditable, size: sizeEditable, country: countryEditable },
        organization ?? {}
      );
      try {
        await editOrganizationInformation({ input });
      } catch {
        setIsSaving(false);
        return;
      }

      // Best-effort refresh of the shared current-user cache so the card reflects the new values.
      // A failed refetch must not strand a save that already succeeded — the query refetches on next mount.
      await queryClient.invalidateQueries({ queryKey: ["DashboardUserCurrent"] }).catch(() => undefined);

      setIsSaving(false);
      onSuccess();
    },
    [editOrganizationInformation, queryClient, onSuccess, websiteEditable, sizeEditable, countryEditable, organization]
  );

  const overlay = createDrawerOverlay({
    loading: { message: "Saving changes...", description: "Please wait, do not close this screen." },
    error: { message: mutation.error?.message || "Failed to save changes...", description: "Please try again later." },
    success: { message: "Organization information updated!" },
  })({ isLoading: isSaving || mutation.isLoading, isError: mutation.isError, isSuccess: mutation.isSuccess });

  if (overlay) return <RpaDrawerOverlay overlay={false} {...overlay} />;

  return (
    <form id={FORM_ID} onSubmit={form.onSubmit((values) => void handleSubmit(values))}>
      <div className={classes.body}>
        <RpaDrawerField label="Organization">{organizationName}</RpaDrawerField>

        {websiteEditable ? (
          <RpaTextInput label="Website" placeholder="Enter" {...form.getInputProps("website")} />
        ) : (
          <RpaDrawerField label="Website">{organization?.website}</RpaDrawerField>
        )}

        {sizeEditable ? (
          <RpaDropdown
            label="Size"
            placeholder="Select.."
            data={ORGANIZATION_SIZE_OPTIONS}
            {...form.getInputProps("size")}
          />
        ) : (
          <RpaDrawerField label="Size">{organization?.size}</RpaDrawerField>
        )}

        {countryEditable ? (
          <RpaDropdown
            label="Country"
            placeholder="Select.."
            searchable
            nothingFound="No countries found"
            data={countrySelectData}
            {...form.getInputProps("country")}
          />
        ) : (
          <RpaDrawerField label="Country">{getCountryLabel(organization?.country)}</RpaDrawerField>
        )}
      </div>

      <RpaDrawer.Footer>
        <div className={classes.footer}>
          <Button form={FORM_ID} type="submit" variant="filled" intent="primary" size="large" fullWidth>
            Save Changes
          </Button>
          <RpaButtonLink data-testid="cancel-edit-organization" onClick={onCancel}>
            Cancel
          </RpaButtonLink>
        </div>
      </RpaDrawer.Footer>
    </form>
  );
};
